<template>
    <div
        v-if="tempSubAccount && subAccount"
        class="flex flex-col h-full px-4 pt-3 pb-8 w-full shadow rounded overflow-y-auto overflow-x-auto"
    >
        <div>
            <sub-account-editor
                :sub-account="tempSubAccount"
                :new-invoice="newInvoice"
                @created="refresh"
                @updated="refresh"
                @deleted="refresh"
            />
        </div>
        <div class="h-full mt-2">
            <div class="flex py-2 items-center gap-2">
                <open-post-filter-selector v-model="isOpen"/>
                <date-range-selector
                    v-model="dateRange"
                    :show-start-date-picker="!isOpen"
                />
            </div>
            <sub-account-details
                v-if="ledger"
                :ledger-entry="ledger"
                :sub-account-filter="ledgerFilter"
                class="bg-base-100 min-h-[250px]"
                :is-customer="isCustomer"
            />
        </div>
    </div>
    <div v-else class="flex items-center justify-center h-full">
        Beklager, finner ikke kunde/leverandør {{ subAccountUuid }}
    </div>
</template>

<script setup lang="ts">
import {computed, inject, onMounted, onUnmounted, Ref, ref, watch} from 'vue'
import {CUSTOMER, SubAccount} from '@/subaccount/types/SubAccount'
import {useEmitter} from '@/services/infrastructure/emitter'
import {DELETE_TRANSACTION, TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import {ORG_SELECTED, ORG_SWITCHED} from '@/types/events'
import {usePanelManager} from '@tritt/multipanel'
import {fetchCustomers, fetchSuppliers, findSubAccountByUuid, useSubAccounts, validateAddressAndContactInfo} from '@/subaccount/subAccount.service'
import {defaultSearchParams} from '@/types/transaction/TransactionSearchParams'
import {orgSelectionComplete} from '@/tenants/org.service'
import SubAccountDetails from '@/subaccount/outstandingBalance/SubAccountDetails.vue'
import OpenPostFilterSelector from '@/subaccount/outstandingBalance/OpenPostFilterSelector.vue'
import DateRangeSelector from '@/dateUtils/DateRangeSelector.vue'
import {SubAccountLedgerFilter} from '@/subaccount/outstandingBalance/SubAccountLedgerFilter'
import {DateRange} from '@/dateUtils/DateRange'
import {LedgerSubAccount} from '@/subaccount/types/SubAccountLedger'
import {useTargetingLinkService} from '@tritt/multipanel'
import {useInvoiceCreator} from '@/invoice/invoice.service'
import {INVOICE_CREATOR} from '@/invoice'
import SubAccountEditor from '@/subaccount/SubAccountEditor.vue'
import {cloneDeep} from 'lodash'

const ledger = ref<LedgerSubAccount | undefined>()
const tempSubAccount = ref<SubAccount | undefined>()
const {fetchSubAccountLedger, customers, suppliers} = useSubAccounts()

const defaultRange: DateRange = {
    start: new Date(defaultSearchParams().fromDate!),
    end: new Date(defaultSearchParams().toDate!)
}

const isOpen = ref(true)
const dateRange = ref<DateRange>(defaultRange)
const ledgerFilter = computed<SubAccountLedgerFilter>(() => {
    return {
        start: dateRange.value.start,
        end: dateRange.value.end,
        isOpen: isOpen.value
    }
})

const {onEvent, cleanUp} = useEmitter()

const props = defineProps({
    subAccountUuid: {
        type: String,
        required: true
    }
})

const isCustomer = computed(() => {
    return (
        tempSubAccount.value?.relationType === CUSTOMER
    )
})

const subAccountUuid = computed(() => {
    return props.subAccountUuid
})

const subAccount = computed(() => {
    return [...customers.value, ...suppliers.value].find(
        (subAcc) => subAcc.uuid === subAccountUuid.value
    )
})

const setTempSubAccount = () => {
    if (subAccount.value) {
        tempSubAccount.value = cloneDeep(subAccount.value)
        validateAddressAndContactInfo(tempSubAccount.value)
    } else {
        tempSubAccount.value = undefined
    }
}

const fetchLedgerForSubAccount = async () => {
    if (tempSubAccount.value?.relationType) {
        const updatedLedger = await fetchSubAccountLedger(
            tempSubAccount.value?.relationType,
            subAccountUuid.value,
            ledgerFilter.value
        )
        if (updatedLedger) {
            ledger.value = updatedLedger
        }
    } else {
        console.warn('subAccountRelationType is not set')
    }
}
const currentPanel = inject('panelId') as Ref<number>

const newInvoice = () => {
    const {targetPanel} = useTargetingLinkService()
    const {invoiceData} = useInvoiceCreator()
    if (tempSubAccount.value && tempSubAccount.value.uuid)
        invoiceData.value.customer = findSubAccountByUuid(tempSubAccount.value.uuid)
    const {setPanelPath} = usePanelManager()
    setPanelPath({
        ...INVOICE_CREATOR,
        panelId: targetPanel.value || currentPanel.value
    })
}

watch(subAccount, async () => {
    setTempSubAccount()
    await fetchLedgerForSubAccount()
})

watch([dateRange, isOpen], async () => {
    await fetchLedgerForSubAccount()
})

const refresh = () => {
    if (subAccount.value?.relationType === CUSTOMER) {
        fetchCustomers()
    } else {
        fetchSuppliers()
    }
}

onMounted(async () => {
    await orgSelectionComplete
    setTempSubAccount()
    await fetchLedgerForSubAccount()
    onEvent(ORG_SELECTED, fetchLedgerForSubAccount)
    onEvent(TRANSACTION_CREATED, fetchLedgerForSubAccount)
    onEvent(TRANSACTION_UPDATED, fetchLedgerForSubAccount)
    onEvent(DELETE_TRANSACTION, fetchLedgerForSubAccount)
    onEvent(ORG_SWITCHED, fetchLedgerForSubAccount)
})

onUnmounted(cleanUp)
</script>

<style>

</style>
